import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const parseMin = (t) => { const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return 0; let h = +m[1] % 12; if (/pm/i.test(m[3])) h += 12; return h * 60 + +m[2]; };
const TYPE_ICON = { arrival: "DoorOpen", homework: "BookOpen", main: "Zap", break: "GlassWater", chill: "Sofa", wrapup: "Waves" };
const STATUS_STYLE = { completed: "bg-emerald-500 text-white", started: "bg-gold text-navy", skipped: "bg-slate-500 text-white", modified: "bg-turquoise text-navy", pending: "bg-white/10 text-white/60" };
const PHILOSOPHY = ["Guided play", "Creativity", "Movement", "Learning", "Friendship", "Exploration", "Confidence-building"];

export default function Program() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [day, setDay] = useState(null);
  const [noteOpen, setNoteOpen] = useState(null);
  const [nowMin, setNowMin] = useState(new Date().getHours() * 60 + new Date().getMinutes());

  const load = () => api.get(`/program/day?date=${date}`).then((r) => setDay(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => { const t = setInterval(() => setNowMin(new Date().getHours() * 60 + new Date().getMinutes()), 30000); return () => clearInterval(t); }, []);

  const blocks = day?.blocks || [];
  const currentIdx = useMemo(() => {
    if (date !== todayISO()) return -1;
    for (let i = 0; i < blocks.length; i++) { if (nowMin >= parseMin(blocks[i].start) && nowMin < parseMin(blocks[i].end)) return i; }
    return -1;
  }, [blocks, nowMin, date]);

  const setStatus = async (b, status) => { try { await api.post(`/program/day/${date}/block/${b.id}`, { status }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveNote = async (b, note) => { try { await api.post(`/program/day/${date}/block/${b.id}`, { note }); setNoteOpen(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleCheck = async (phase, key, value) => { try { await api.post(`/program/day/${date}/checklist`, { phase, key, value }); load(); } catch (e) { toast.error(apiErr(e)); } };

  const cur = currentIdx >= 0 ? blocks[currentIdx] : null;
  const next = currentIdx >= 0 && currentIdx + 1 < blocks.length ? blocks[currentIdx + 1] : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white pb-20">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10 sticky top-0 bg-slate-900 z-30">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">DAILY PROGRAM</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white/10 rounded-lg px-2 py-1 text-sm text-white" data-testid="program-date" />
          <Button variant="ghost" size="sm" className="text-white" onClick={() => window.print()} data-testid="program-print">Print</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : user?.role === "watcher" ? "/watcher" : "/staff")} data-testid="program-back">Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="program-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {day?.no_program && <div className="bg-white/5 rounded-2xl p-8 text-center text-white/60" data-testid="program-noprogram">No program scheduled on {day.weekday_name}. The after-school program runs Monday–Friday.</div>}

        {day && !day.no_program && (
          <>
            <div className="rounded-2xl bg-gradient-to-br from-ocean to-navy border border-turquoise/20 p-5" data-testid="program-hero">
              <p className="text-turquoise text-xs tracking-widest font-bold">TODAY AT ADVENTURE ROCK · {day.weekday_name?.toUpperCase()}</p>
              <h1 className="font-display font-black text-3xl mt-1">{day.theme_tag}</h1>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-white/5 rounded-xl p-2"><p className="text-2xl font-black text-gold">{day.expected}</p><p className="text-[10px] text-white/50">Expected</p></div>
                <div className="bg-white/5 rounded-xl p-2"><p className="text-2xl font-black text-turquoise">{day.checked_in}</p><p className="text-[10px] text-white/50">Checked in</p></div>
                <div className="bg-white/5 rounded-xl p-2"><p className="text-2xl font-black">{blocks.filter((b) => b.status === "completed").length}/{blocks.length}</p><p className="text-[10px] text-white/50">Blocks done</p></div>
              </div>
            </div>

            {cur && (
              <div className="rounded-2xl bg-gold text-navy p-5" data-testid="program-current">
                <p className="text-xs font-bold tracking-widest">NOW · {cur.start}–{cur.end}</p>
                <p className="font-display font-black text-2xl">{cur.title}</p>
                {cur.planned_activity && <p className="font-semibold text-sm mt-1">▶ {cur.planned_activity}</p>}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button onClick={() => setStatus(cur, "started")} className="bg-navy text-white rounded-full font-bold" data-testid="btn-start-current"><Icons.Play className="w-4 h-4 mr-1" /> Start</Button>
                  <Button onClick={() => setStatus(cur, "completed")} className="bg-emerald-600 text-white rounded-full font-bold" data-testid="btn-complete-current"><Icons.Check className="w-4 h-4 mr-1" /> Complete</Button>
                </div>
                {next && <p className="text-navy/70 text-xs mt-3">Next: {next.start} · {next.title}</p>}
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-white/50">
              <Icons.User className="w-4 h-4 text-turquoise" />
              <span>Lead: <b className="text-white">{day.role_names?.lead || "—"}</b></span> ·
              <span>Activity: <b className="text-white">{day.role_names?.activity || "—"}</b></span> ·
              <span>Support: <b className="text-white">{day.role_names?.support || "—"}</b></span>
            </div>

            <div className="space-y-2">
              {blocks.map((b, i) => {
                const Icon = Icons[TYPE_ICON[b.type]] || Icons.Circle;
                const isCur = i === currentIdx;
                return (
                  <div key={b.id} className={`rounded-2xl p-4 border ${isCur ? "border-gold bg-gold/5" : "border-white/10 bg-white/5"}`} data-testid={`block-${b.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-turquoise" /><p className="font-bold">{b.title}</p><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[b.status] || STATUS_STYLE.pending}`} data-testid={`block-status-${b.id}`}>{b.status}</span></div>
                        <p className="text-xs text-white/50 mt-0.5">{b.start} – {b.end}</p>
                        {b.planned_activity && <p className="text-sm text-gold font-semibold mt-1">▶ {b.planned_activity}</p>}
                        <p className="text-xs text-white/60 mt-1">{b.description}</p>
                        {b.examples?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{b.examples.map((e) => <span key={e} className="text-[10px] bg-white/10 rounded px-1.5 py-0.5">{e}</span>)}</div>}
                        {b.note && <p className="text-[11px] text-turquoise mt-2 italic">📝 {b.note}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {["started", "completed", "skipped", "modified"].map((s) => (
                        <button key={s} onClick={() => setStatus(b, s)} data-testid={`block-${b.id}-${s}`} className={`text-xs rounded-full px-3 py-1 font-semibold capitalize ${b.status === s ? STATUS_STYLE[s] : "bg-white/10 text-white/60"}`}>{s}</button>
                      ))}
                      <button onClick={() => setNoteOpen(noteOpen === b.id ? null : b.id)} className="text-xs rounded-full px-3 py-1 bg-white/10 text-white/60" data-testid={`block-${b.id}-note-btn`}><Icons.PenLine className="w-3 h-3 inline" /> Note</button>
                    </div>
                    {noteOpen === b.id && (
                      <div className="mt-2">
                        <Textarea defaultValue={b.note} id={`note-${b.id}`} rows={2} className="bg-white/10 border-white/20 text-white rounded-xl text-sm" placeholder="e.g. Inflatable relay was shortened due to late arrivals." data-testid={`block-${b.id}-note-input`} />
                        <Button size="sm" className="mt-1 rounded-full bg-turquoise text-navy font-bold" onClick={() => saveNote(b, document.getElementById(`note-${b.id}`).value)} data-testid={`block-${b.id}-note-save`}>Save note</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-2xl p-4" data-testid="prep-checklist">
                <p className="font-display font-bold mb-2 flex items-center gap-2"><Icons.ListChecks className="w-4 h-4 text-turquoise" /> Daily Prep</p>
                {day.prep_items?.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`prep-${it.key}`}>
                    <input type="checkbox" checked={it.done} onChange={(e) => toggleCheck("prep", it.key, e.target.checked)} className="accent-turquoise w-4 h-4" />
                    <span className={it.done ? "line-through text-white/40" : "text-white/80"}>{it.label}</span>
                  </label>
                ))}
              </div>
              <div className="bg-white/5 rounded-2xl p-4" data-testid="closing-checklist">
                <p className="font-display font-bold mb-2 flex items-center gap-2"><Icons.ListChecks className="w-4 h-4 text-gold" /> End of Day</p>
                {day.closing_items?.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`closing-${it.key}`}>
                    <input type="checkbox" checked={it.done} onChange={(e) => toggleCheck("closing", it.key, e.target.checked)} className="accent-gold w-4 h-4" />
                    <span className={it.done ? "line-through text-white/40" : "text-white/80"}>{it.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-turquoise/10 to-transparent border border-turquoise/20 rounded-2xl p-5">
              <p className="font-display font-black text-lg">Adventure Rock is not just a room with kids.</p>
              <div className="flex flex-wrap gap-2 mt-2">{PHILOSOPHY.map((p) => <span key={p} className="text-xs bg-turquoise/15 text-turquoise rounded-full px-2.5 py-1 font-semibold">{p}</span>)}</div>
              <p className="text-white/60 text-xs mt-3">Children should leave feeling like they experienced something — guided play, not unstructured free play. Offer choices within the planned activity ("It's Creator's Cave time — paint or build?"), and always lead inflatable play with a structured game.</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
